<script lang="ts">
import { defineComponent } from 'vue'

// eslint-disable-next-line no-restricted-syntax
export enum TooltipPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export default defineComponent({
  name: 'SancareOcticon',
})
</script>

<script lang="ts" setup>
import octicons, { type IconHeight, type IconSize } from '@primer/octicons'
import { computed, onBeforeMount } from 'vue'

export interface IconHeightWithRawPaths<N extends IconSize> extends IconHeight<N> {
  rawPaths: string[]
  // Font Awesome icons (e.g. the vendored "robot" icon below) are drawn with the nonzero
  // winding rule, unlike octicons which rely on evenodd for their cutouts.
  fillRule?: 'evenodd' | 'nonzero'
}

export interface SancareOcticonProps {
  height?: number | null
  name: string
  tooltipText?: string | null
  tooltipPosition?: TooltipPosition
  width?: number | null
}

// Font Awesome's "robot" solid icon (used across the app, e.g. PredictionIcon, to flag
// AI-predicted content) — vendored here as octicons has no robot icon of its own.
const customIcons = {
  robot: {
    symbol: 'robot',
    keywords: ['robot', 'ai'],
    heights: {
      16: {
        width: 16,
        path: '<path d="M0 256v128c0 17.7 14.3 32 32 32h32V224H32c-17.7 0-32 14.3-32 32zM464 96H352V32c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H176c-44.2 0-80 35.8-80 80v272c0 35.3 28.7 64 64 64h320c35.3 0 64-28.7 64-64V176c0-44.2-35.8-80-80-80zM256 416h-64v-32h64v32zm-32-120c-22.1 0-40-17.9-40-40s17.9-40 40-40 40 17.9 40 40-17.9 40-40 40zm128 120h-64v-32h64v32zm96 0h-64v-32h64v32zm-32-120c-22.1 0-40-17.9-40-40s17.9-40 40-40 40 17.9 40 40-17.9 40-40 40zm192-72h-32v192h32c17.7 0 32-14.3 32-32V256c0-17.7-14.3-32-32-32z"></path>',
        fillRule: 'nonzero' as const,
        options: {
          version: '1.1',
          width: 640,
          height: 512,
          viewBox: '0 0 640 512',
          class: 'octicon octicon-robot',
          'aria-hidden': 'true',
        },
      },
    },
  },
}

const props = withDefaults(defineProps<SancareOcticonProps>(), {
  height: null,
  tooltipText: null,
  tooltipPosition: TooltipPosition.BOTTOM,
  width: null,
})

if (!props.name || !(customIcons[props.name] || octicons[props.name])) {
  // eslint-disable-next-line no-console
  console.warn(`Invalid octicon name provided: ${props.name}`)
}

onBeforeMount(() => {
  const pathRegex = /d="([^"]+)"/gi
  Object.values({ ...octicons, ...customIcons }).forEach((icon) => {
    Object.values(icon.heights).forEach((iconVersion) => {
      const iconVersionWithPaths = iconVersion as unknown as IconHeightWithRawPaths<IconSize>
      iconVersionWithPaths.rawPaths = []
      let res: RegExpExecArray | null
      do {
        res = pathRegex.exec(iconVersion.path)
        if (res) {
          iconVersionWithPaths.rawPaths.push(res[1])
        }
      } while (res)
    })
  })
})

const selectedIcon = computed(() => {
  const icon = customIcons[props.name] || octicons[props.name] || octicons.x
  const availableHeights = Object.keys(icon.heights).map((h) => parseInt(h, 10))
  const userSize = props.height || props.width || 16
  const bestHeight = availableHeights.reduce((acc, h) => (h <= userSize ? h : acc), availableHeights[0])

  return icon.heights[bestHeight]
})

const computedWidth = computed(() => {
  if (props.width || props.height) {
    return props.width
      ? props.width
      : (props.height * selectedIcon.value.options.width) / selectedIcon.value.options.height
  }
  return selectedIcon.value.options.width
})

const computedHeight = computed(() => {
  if (props.width || props.height) {
    return props.height
      ? props.height
      : (props.width * selectedIcon.value.options.height) / selectedIcon.value.options.width
  }
  return selectedIcon.value.options.height
})

const tooltip = computed(() => {
  if (props.tooltipText) {
    return {
      content: props.tooltipText,
      placement: props.tooltipPosition,
    }
  }

  return {}
})
defineExpose({ tooltip })
</script>

<template>
  <svg
    v-tooltip="tooltip"
    :class="selectedIcon.options.class"
    :version="selectedIcon.options.version"
    :width="computedWidth"
    :height="computedHeight"
    :viewBox="selectedIcon.options.viewBox"
    aria-hidden="true"
  >
    <path
      v-for="path in selectedIcon.rawPaths"
      :key="path"
      :fill-rule="selectedIcon.fillRule || 'evenodd'"
      :d="path"
    />
  </svg>
</template>
